import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ReactNode } from 'react';
import { Controller, type Control } from 'react-hook-form';

import { type OnboardingFormValues } from '@/domain/onboarding/types';

type TextFieldName = Exclude<keyof OnboardingFormValues, 'acceptTerms'>;

interface Props extends Omit<TextFieldProps, 'name' | 'label' | 'error'> {
  name: TextFieldName;
  control: Control<OnboardingFormValues>;
  label: string;
  optionalLabel?: string;
  hint?: string;
  /** Reformats the value when the field loses focus (document, phone). */
  formatOnBlur?: (value: string) => string;
  children?: ReactNode;
}

/** Labelled, controlled text input with the field error underneath (same anatomy as the web forms). */
const OnboardingField = ({
  name,
  control,
  label,
  optionalLabel,
  hint,
  formatOnBlur,
  children,
  ...textFieldProps
}: Props) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <FormControl fullWidth>
        <FormLabel htmlFor={name}>
          {label}
          {optionalLabel ? (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
              {optionalLabel}
            </Typography>
          ) : null}
        </FormLabel>
        <TextField
          {...textFieldProps}
          {...field}
          id={name}
          fullWidth
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? hint}
          onBlur={() => {
            if (formatOnBlur && field.value) field.onChange(formatOnBlur(String(field.value)));
            field.onBlur();
          }}
        >
          {children}
        </TextField>
      </FormControl>
    )}
  />
);

export default OnboardingField;
